import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, LoginRequest } from '../types';
import { authApi } from '../services/api';
import { storage } from '../utils';
import { message } from 'antd';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthProvider = (): AuthContextType => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  useEffect(() => {
    const initAuth = async () => {
      const token = storage.getToken();
      const savedUser = storage.getUser();

      if (token && savedUser) {
        // For now, just use saved user since backend doesn't have profile endpoint
        setUser(savedUser);
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await authApi.login(credentials);
      
      // Backend returns { token: { accessToken, refreshToken }, user: { username, fullName, roles } }
      storage.setToken(response.token.accessToken);
      
      // Convert backend user format to frontend format
      const user: User = {
        id: response.user.username, // Use username as id
        username: response.user.username,
        fullName: response.user.fullName,
        roles: response.user.roles,
        activated: true, // Assume activated if login successful
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      };
      
      storage.setUser(user);
      setUser(user);
      
      message.success('Login successful!');
      return true;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      message.error(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    storage.clear();
    setUser(null);
    message.success('Logout successful!');
  };

  const refreshUser = async (): Promise<void> => {
    // Backend doesn't have profile endpoint, so we can't refresh user data
    // Just keep current user or logout if needed
    const savedUser = storage.getUser();
    if (!savedUser) {
      logout();
    }
  };

  return {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshUser,
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuthProvider();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};


